import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import toast from 'react-hot-toast'
import { loginUrl } from './auth-navigation'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

export function getApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError<unknown>(error)) {
    const data = error.response?.data
    if (data && typeof data === 'object') {
      if ('error' in data && typeof data.error === 'string' && data.error.trim()) {
        return data.error
      }
      if ('detail' in data && typeof data.detail === 'string' && data.detail.trim()) {
        return data.detail
      }
    }
    if (error.response) {
      return `O servidor recusou o pedido (HTTP ${error.response.status}). Tente novamente ou contacte o responsável.`
    }
  }
  return 'Não foi possível contactar o servidor. Verifique a ligação e tente novamente.'
}

// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - Handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshToken = localStorage.getItem('refresh_token')

      if (!refreshToken) {
        // No refresh token → redirect to login without toast
        localStorage.removeItem('access_token')
        window.location.href = loginUrl(window.location.pathname + window.location.search)
        return Promise.reject(error)
      }

      try {
        // Try to refresh token
        const response = await axios.post(`${API_URL}/api/auth/refresh`, {
          refresh_token: refreshToken,
        })

        const { access_token, refresh_token: newRefreshToken } = response.data
        localStorage.setItem('access_token', access_token)
        localStorage.setItem('refresh_token', newRefreshToken)

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`
        }
        return apiClient(originalRequest)
      } catch (_refreshError) {
        // Refresh failed → clean up and redirect to login without toast
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = loginUrl(window.location.pathname + window.location.search)
        return Promise.reject(_refreshError)
      }
    }

    // Handle other errors (non-401, or already-retried 401)
    const errorMessage = getApiErrorMessage(error)

    // Suppress toast for auth-related 401 on retry (avoid double-toasting)
    if (error.response?.status !== 401) {
      toast.error(errorMessage)
    }
    return Promise.reject(error)
  }
)

// API helper functions
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    apiClient.get<T>(url, config),
  
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    apiClient.post<T>(url, data, config),
  
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    apiClient.put<T>(url, data, config),
  
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    apiClient.patch<T>(url, data, config),
  
  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    apiClient.delete<T>(url, config),
}

export default apiClient
